import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import {
    getSiteProductDefinition,
    SITE_FEATURES,
    type SiteFeatureDefinition,
} from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import { DialogBody, DialogHeader, Heading } from '@gitbook/spine-react';

import { getSiteVisibilityLabel } from '../../../../../Components';
import { apiQuery, useSiteContext } from '../../../../../state';

export function SiteUpgradeModalStepDowngrade(props: { from: api.SiteType; to: api.SiteType }) {
    const { from, to } = props;
    const { title: fromTitle } = getSiteProductDefinition(from);
    const { title: toTitle } = getSiteProductDefinition(to);

    return (
        <>
            <DialogHeader>
                <Heading>Downgrade your site</Heading>
            </DialogHeader>
            <DialogBody className="flex max-w-112 flex-col items-start justify-start gap-4 text-muted ">
                By downgrading to {toTitle}, your site will lose the following features from the{' '}
                {fromTitle} plan:
                <React.Suspense fallback={null}>
                    <FeaturesTable from={from} to={to} />
                </React.Suspense>
            </DialogBody>
        </>
    );
}

function FeaturesTable(props: { to: api.SiteType; from: api.SiteType }) {
    const { to, from } = props;
    const { site, organizationId } = useSiteContext();
    const { data: siteSettings } = useSuspenseQuery(
        apiQuery('getSiteRawCustomizationById', { organizationId, siteId: site.id })
    );

    // Create a list of features that will be lost when downgrading.
    const features = SITE_FEATURES.filter((feature) => feature[from] && !feature[to]);

    return (
        <ul
            style={{
                margin: 0,
                padding: 0,
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            }}
        >
            {features.map((feature) => {
                return (
                    <FeatureTableRow
                        title={feature.title}
                        fallback={
                            <FeatureFallback
                                feature={feature}
                                site={site}
                                siteSettings={siteSettings}
                            />
                        }
                    />
                );
            })}
        </ul>
    );
}

function FeatureFallback(props: {
    feature: SiteFeatureDefinition;
    site: api.Site;
    siteSettings: api.SiteCustomizationSettings;
}): React.ReactElement | null {
    const { feature, site, siteSettings } = props;

    switch (feature.key) {
        case api.SiteFeatureKey.SitesAdvancedCustomization:
            return siteSettings.advancedCustomization.enabled ? (
                <>Your site's advanced customization will be disabled.</>
            ) : null;
        case api.SiteFeatureKey.SitesShareLinks:
            return site.visibility === api.SiteVisibility.ShareLink ? (
                <>
                    Your site visibility will revert to{' '}
                    <strong>{getSiteVisibilityLabel(api.SiteVisibility.Public)}</strong>.
                </>
            ) : null;
        case api.SiteFeatureKey.SitesVisitorAuthentication:
            return site.visibility === api.SiteVisibility.VisitorAuth ? (
                <>
                    Your site visibility will revert to{' '}
                    <strong>{getSiteVisibilityLabel(api.SiteVisibility.Public)}</strong>.
                </>
            ) : null;
        default:
            return null;
    }
}

function FeatureTableRow(props: { title: string; fallback?: React.ReactNode }) {
    const { title, fallback } = props;
    return (
        <li className="flex items-start gap-2 p-2">
            <Icon.X size={18} className="m-nudge-icon shrink-0 text-danger" />
            <div>
                <div>{title}</div>
                {fallback && <p className="text-muted text-ui-small">{fallback}</p>}
            </div>
        </li>
    );
}
